import { useQuery } from '@tanstack/react-query'
import {
  Download,
  Smartphone,
  Calendar,
  HardDrive,
  Info,
  AlertCircle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react'
import { android, type ApkInfoResponse } from '../api/client'

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function formatFileSize(sizeMb: number): string {
  if (sizeMb < 1) {
    return `${(sizeMb * 1024).toFixed(0)} KB`
  }
  return `${sizeMb.toFixed(1)} MB`
}

function getAndroidVersionName(sdkVersion: number): string {
  const versions: Record<number, string> = {
    21: 'Lollipop (5.0)',
    22: 'Lollipop (5.1)',
    23: 'Marshmallow (6.0)',
    24: 'Nougat (7.0)',
    25: 'Nougat (7.1)',
    26: 'Oreo (8.0)',
    27: 'Oreo (8.1)',
    28: 'Pie (9.0)',
    29: 'Android 10',
    30: 'Android 11',
    31: 'Android 12',
    32: 'Android 12L',
    33: 'Android 13',
    34: 'Android 14',
    35: 'Android 15',
  }
  return versions[sdkVersion] || `Android SDK ${sdkVersion}`
}

export default function Downloads() {
  const {
    data: apkInfo,
    isLoading,
    error,
    refetch,
  } = useQuery<ApkInfoResponse>({
    queryKey: ['android-apk-info'],
    queryFn: () => android.getInfo(),
    retry: false,
  })

  const handleDownload = () => {
    if (apkInfo) {
      window.location.href = android.getDownloadUrl()
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 rounded-xl bg-sky-500/20">
          <Smartphone size={32} className="text-sky-400" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Downloads</h1>
          <p className="text-dark-400 text-sm">
            Download the S3ND Media Android app
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="card p-8 text-center">
          <RefreshCw size={32} className="animate-spin mx-auto text-sky-400 mb-4" />
          <p className="text-dark-400">Loading APK information...</p>
        </div>
      )}

      {error && (
        <div className="card p-8 text-center border border-red-500/20">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2 text-red-400">
            APK Not Available
          </h3>
          <p className="text-dark-400 mb-4">
            The Android APK is not currently available for download.
            <br />
            Please contact the administrator to upload a build.
          </p>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      )}

      {apkInfo && (
        <div className="space-y-6">
          {/* Main Download Card */}
          <div className="card p-6 sm:p-8 border border-sky-500/20 bg-gradient-to-br from-dark-800 to-dark-900">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="shrink-0 p-4 rounded-2xl bg-sky-500/20">
                <Smartphone size={48} className="text-sky-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold">S3ND Media</h2>
                  <span className="px-3 py-1 rounded-full bg-sky-500/20 text-sky-400 text-sm font-medium">
                    v{apkInfo.version_name}
                  </span>
                </div>
                <p className="text-dark-400 mb-4">
                  Stream your music and video library on the go with the official
                  Android app.
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-dark-400">
                  <div className="flex items-center gap-1.5">
                    <HardDrive size={14} />
                    <span>{formatFileSize(apkInfo.file_size_mb)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar size={14} />
                    <span>{formatDate(apkInfo.build_date)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Info size={14} />
                    <span>
                      Requires {getAndroidVersionName(apkInfo.min_sdk_version)}+
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="btn btn-primary w-full sm:w-auto px-8 py-3 text-lg font-semibold flex items-center justify-center gap-2"
              >
                <Download size={20} />
                Download APK
              </button>
            </div>
          </div>

          {/* Changelog Section */}
          {apkInfo.changelog && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                What's New in v{apkInfo.version_name}
              </h3>
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-dark-300 font-sans text-sm leading-relaxed bg-dark-950/50 p-4 rounded-lg">
                  {apkInfo.changelog}
                </pre>
              </div>
            </div>
          )}

          {/* Installation Instructions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Installation Guide</h3>
            <ol className="space-y-4 text-dark-300">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-sm font-medium flex items-center justify-center">
                  1
                </span>
                <span>
                  Download the APK file by clicking the button above.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-sm font-medium flex items-center justify-center">
                  2
                </span>
                <span>
                  On your Android device, go to{' '}
                  <strong className="text-white">
                    Settings → Security → Install unknown apps
                  </strong>{' '}
                  and enable installation from your browser or file manager.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-sm font-medium flex items-center justify-center">
                  3
                </span>
                <span>
                  Open the downloaded APK file and tap{' '}
                  <strong className="text-white">Install</strong>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-sm font-medium flex items-center justify-center">
                  4
                </span>
                <span>
                  Launch the app and log in with your S3ND Media account.
                </span>
              </li>
            </ol>
          </div>

          {/* Features Section */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Features</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">
                  Stream music with background playback
                </span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">
                  Browse artists, albums, and playlists
                </span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">Watch movies and TV shows</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">
                  Discover and explore global libraries
                </span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">Import from Spotify</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-dark-300">
                  Material You design with dark theme
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
